import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback } from 'react';
import { generatePath } from 'react-router';
import * as yup from 'yup';
import { ContentModel, toNftContent } from '~/components/Content/Content.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { HttpError } from '~/components/Error/HttpError';
import { EvolveBottomSheet } from '~/components/Evolve/Popup/EvolveBottomSheet';
import { friendsMap } from '~/components/Friend/Friend.hooks';
import { useBottomSheet } from '~/components/Modal/BottomSheet';
import { useSearchParams } from '~/components/Route';

export type EvolveValues = {
  returnUri?: string;
  tokenIdList: (string | undefined)[];
  tokenType?: string;
};

export const useValidate = () =>
  useCallback((values: EvolveValues) => {
    const errors: Record<string, string> = {};
    const { tokenIdList } = values;
    if (tokenIdList.includes(undefined) || tokenIdList.length !== 3) {
      errors.tokenIdList = 'NOT_ENOUGH_SELECTED_NFTS';
    }
    return errors;
  }, []);

export type EvolveToken = {
  authUri: string;
  evolutionId: number;
};

export type EvolutionStatus = {
  status: 'REQUESTED' | 'CONFIRM_TRANSACTION' | 'AIRDROP_REQUESTED' | 'COMPLETED' | 'FAILED';
  evolvedGrowth: string;
  nftId: string;
  bonusBoost: number;
  boost: number;
} & Partial<ContentModel>;

export const issueTokenUrl = '/api/citizen/v1/pet/evolution';
export const tfaReturnUri = `${document.location.origin}/auth/tfa/verify`;
export const issueToken = async ({ returnUri = tfaReturnUri, ...values }: EvolveValues) => {
  const { data } = await axios.post<EvolveToken>(issueTokenUrl, { returnUri, ...values });
  return data;
};

export const useIssueToken = (options?: UseMutationOptions<EvolveToken, HttpError, EvolveValues>) =>
  useMutation<EvolveToken, HttpError, EvolveValues>({
    mutationFn: issueToken,
    ...options,
  });

export const evolveUrl = '/api/citizen/v1/pet/evolution/:evolutionId/confirm';
export const evolve = async (values: EvolveToken) => {
  await axios.post<undefined>(generatePath(evolveUrl, values));
  return null;
};

export const useEvolve = (options?: UseMutationOptions<null, HttpError, EvolveToken>) =>
  useMutation<null, HttpError, EvolveToken>({
    mutationFn: evolve,
    ...options,
  });

export const evolutionStatusUrl = '/api/citizen/v1/pet/evolution/:evolutionId/status';
export const getEvolutionStatus = async (evolutionId: EvolveToken['evolutionId']) => {
  const { data } = await axios.get<EvolutionStatus>(
    generatePath(evolutionStatusUrl, { evolutionId })
  );

  if (data.status === 'FAILED') throw new BaseError('FAILED', data);

  return data.contentsUrl
    ? ({
        contentsType: 'VIDEO',
        ...data,
        ...toNftContent(data as ContentModel),
      } as EvolutionStatus)
    : data;
};
export const useEvolutionStatus = (
  evolutionId: EvolveToken['evolutionId'],
  options?: UseQueryOptions<EvolutionStatus, HttpError>
) =>
  useQuery<EvolutionStatus, HttpError>({
    queryKey: ['evolution', evolutionId],
    queryFn: () => getEvolutionStatus(evolutionId),
    ...options,
  });

export const useProceedEvolve = (
  options?: UseMutationOptions<EvolutionStatus, HttpError, EvolveValues>
) => {
  const { open } = useBottomSheet();
  return useMutation<EvolutionStatus, HttpError, EvolveValues>({
    mutationFn: async (values) => {
      const { confirmed, ...data } = await open(<EvolveBottomSheet {...values} />, {
        onBackdropClick: false,
        throwOnDismiss: true,
      });

      return data as EvolutionStatus;
    },
    ...options,
  });
};

export const schema = yup.object().shape({
  tokenType: yup.string().default(friendsMap.CitizenCat.tokenType),
});
export const useEvolveFormParams = () => {
  const converter = useCallback((params) => schema.castOrDefault(params), []);
  return useSearchParams<{ tokenType: string }>({ converter });
};
